import moment from 'moment'
import React, { useEffect, useState } from 'react'
import {
  FaArrowAltCircleRight,
  FaArrowAltCircleLeft,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa'
import { SelectInput } from '@components/common/form'
import { parseMilitaryTimeToAMPM } from '@utils'
import { formatCutoff } from '@utils/groupOrderUtils'

interface MenuItem {
  id: string
  name: string
  description: string
  vendorDescription: string
  itemType: string
  price: number
  chefPrice: number
  retailPrice: number
}

export interface SearchGroupOrderConceptsProps {
  search: string
  availableOn: string
  tagsDietaryPreferenceUnique: string
  excludeTagsDietaryPreferenceUnique: string
  page: number
  limit: number
}

interface SearchGroupOrderConceptsFilters {
  search: string
  availableOn: string
  tagsDietaryPreferenceUnique: string
  excludeTagsDietaryPreferenceUnique: string
}

interface MenuItemAttributes {
  allDietaryPreferences: string[]
}

interface MenuConcept {
  id: string
  name: string
  chef: {
    id: string
    name: string
  }
  hasMenuItemWithMods: boolean
  cuisines: string[]
  cutoffDays: number | null
  cutoffHours: number | null
  maxItems: number | null
  pickupHours: number | null // earliest pickup time
  availableDays: number[]
  minGoPayout: number
  dietaryCounts: Record<string, number>
  menuItems: MenuItem[]
}

interface PickMenuModalProps {
  date: moment.Moment
  searchMenuConcepts: (
    req: SearchGroupOrderConceptsProps,
  ) => Promise<MenuConcept[]>
  loadMenuItemSettings: () => Promise<MenuItemAttributes>
  onPickMenu: (menuConcept: MenuConcept) => Promise<void>
}

const getAvailableDaysStr = (menu: MenuConcept) => {
  if (menu.availableDays.length === 0) {
    return 'N/A'
  }

  const dayNames = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']
  const dayAbbreviations = menu.availableDays.map((day) => dayNames[day])

  return dayAbbreviations.join(', ')
}

const getDietaryCountsArray = (menu: MenuConcept) => {
  return Object.entries(menu.dietaryCounts).map(
    ([dietary, count]) => `${dietary} (${count})`,
  )
}

const PickMenuModal: React.FC<PickMenuModalProps> = ({
  date,
  loadMenuItemSettings,
  onPickMenu,
  searchMenuConcepts,
}) => {
  const [search, setSearch] = useState('')
  const [menuConcepts, setMenuConcepts] = useState<MenuConcept[]>([])
  const [menuItemAttributes, setMenuItemAttributes] =
    useState<MenuItemAttributes>({ allDietaryPreferences: [] })
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [showDietaryCounts, setShowDietaryCounts] = useState(false)
  const [stagedMenus, setStagedMenus] = useState<MenuConcept[]>([])
  const [isProcessingStaged, setIsProcessingStaged] = useState(false)
  const [filteredDietaryPreference, setFilteredDietaryPreference] =
    useState<string>('')
  const [
    filteredExcludeTagsDietaryPreferenceUnique,
    setFilteredExcludeTagsDietaryPreferenceUnique,
  ] = useState<string>('')
  const [filterExcludeTags, setFilterExcludeTags] = useState(false)
  const [lastSearchFilters, setLastSearchFilters] =
    useState<SearchGroupOrderConceptsFilters | null>(null)
  const limit = 25

  const performSearch = async (
    searchTerm: string,
    pageNum: number,
    dietaryFilter: string,
    excludeTagsDietaryPreferenceUnique: string,
  ) => {
    setIsLoading(true)
    const searchFilters: SearchGroupOrderConceptsFilters = {
      search: searchTerm,
      availableOn: date.toISOString(),
      tagsDietaryPreferenceUnique: dietaryFilter,
      excludeTagsDietaryPreferenceUnique,
    }
    let nextPage = pageNum
    if (JSON.stringify(searchFilters) !== JSON.stringify(lastSearchFilters)) {
      nextPage = 0
      setPage(nextPage)
    }
    const results = await searchMenuConcepts({
      ...searchFilters,
      page: nextPage,
      limit,
    })
    if (results) {
      setMenuConcepts(results)
    } else {
      setMenuConcepts([])
    }
    setLastSearchFilters(searchFilters)
    setIsLoading(false)
  }

  useEffect(() => {
    loadMenuItemSettings().then((attributes) => {
      setMenuItemAttributes(attributes)
    })
  }, [loadMenuItemSettings])

  useEffect(() => {
    performSearch('', 0, '', '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearchSubmit = () => {
    performSearch(
      search,
      page,
      filteredDietaryPreference,
      filteredExcludeTagsDietaryPreferenceUnique,
    )
  }

  const handleStageMenu = (menu: MenuConcept) => {
    if (!stagedMenus.find((staged) => staged.id === menu.id)) {
      setStagedMenus((prev) => [...prev, menu])
    }
  }

  const handleRemoveFromStaging = (menuId: string) => {
    setStagedMenus((prev) => prev.filter((menu) => menu.id !== menuId))
  }

  const handleFinalizeStaged = async () => {
    if (stagedMenus.length === 0) {
      return
    }

    setIsProcessingStaged(true)

    try {
      for (const menu of stagedMenus) {
        await onPickMenu(menu)
      }

      // Clear staged menus after successful processing
      setStagedMenus([])
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`handleFinalizeStaged error: ${error}`)
    } finally {
      setIsProcessingStaged(false)
    }
  }

  const getStagingSummary = () => {
    const totalItemCount = stagedMenus.reduce(
      (sum, menu) => sum + menu.menuItems.length,
      0,
    )
    const dietarySummary: Record<string, number> = {}
    const cuisineSummary: Record<string, number> = {}

    stagedMenus.forEach((menu) => {
      Object.entries(menu.dietaryCounts).forEach(([dietary, count]) => {
        dietarySummary[dietary] = (dietarySummary[dietary] || 0) + count
      })
      menu.cuisines.forEach((cuisine) => {
        cuisineSummary[cuisine] = (cuisineSummary[cuisine] || 0) + 1
      })
    })

    return { totalItemCount, dietarySummary, cuisineSummary }
  }

  // TODO: fix pagination by loading all and make table height fixed

  const dietaryPreferenceOptions = [
    { value: '', text: 'All' },
    ...menuItemAttributes.allDietaryPreferences.map((preference) => ({
      value: preference,
      text: preference,
    })),
  ]

  const excludeDietaryPreferenceOptions = [
    { value: '', text: 'None' },
    ...menuItemAttributes.allDietaryPreferences.map((preference) => ({
      value: preference,
      text: preference,
    })),
  ]

  const updateDietaryPreferenceFilters = (dietaryTag: string) => {
    if (filterExcludeTags) {
      setFilteredExcludeTagsDietaryPreferenceUnique(dietaryTag)
      setFilteredDietaryPreference('')
    } else {
      setFilteredDietaryPreference(dietaryTag)
      setFilteredExcludeTagsDietaryPreferenceUnique('')
    }
  }

  return (
    <div className="space-y-6 w-full">
      {/* Search Section */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by menu name, chef, or cuisine"
            className="flex-1 border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
          />
          <button
            onClick={handleSearchSubmit}
            disabled={isLoading}
            className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:bg-gray-400 transition-colors whitespace-nowrap"
          >
            {isLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {/* Filters and Pagination Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Pagination Controls */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              {page > 0 && (
                <button
                  className="p-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={page <= 0}
                  onClick={() => {
                    const newPage = page - 1
                    setPage(newPage)
                    performSearch(
                      search,
                      newPage,
                      filteredDietaryPreference,
                      filteredExcludeTagsDietaryPreferenceUnique,
                    )
                  }}
                  title="Previous page"
                >
                  <FaArrowAltCircleLeft size={18} />
                </button>
              )}
              <span className="px-3 py-1 bg-gray-100 rounded-md text-sm font-medium text-gray-700 min-w-[80px] text-center">
                Page {page + 1}
              </span>
              {menuConcepts.length === limit && (
                <button
                  className="p-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  onClick={() => {
                    const newPage = page + 1
                    setPage(newPage)
                    performSearch(
                      search,
                      newPage,
                      filteredDietaryPreference,
                      filteredExcludeTagsDietaryPreferenceUnique,
                    )
                  }}
                  title="Next page"
                >
                  <FaArrowAltCircleRight size={18} />
                </button>
              )}
            </div>

            {/* Results Count */}
            {menuConcepts.length > 0 && (
              <div className="text-sm text-gray-600">
                Showing {menuConcepts.length} menu
                {menuConcepts.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Filters Section */}
          <div className="flex flex-col sm:flex-row items-end gap-3">
            <SelectInput
              label="Dietary Preference"
              value={filterExcludeTags ? 'exclude' : 'include'}
              options={[
                { value: 'include', text: 'Include' },
                { value: 'exclude', text: 'Exclude' },
              ]}
              onChange={(nextDietaryExclude: string) => {
                if (nextDietaryExclude === 'exclude') {
                  setFilterExcludeTags(true)
                  setFilteredDietaryPreference('')
                } else {
                  setFilterExcludeTags(false)
                  setFilteredExcludeTagsDietaryPreferenceUnique('')
                }
              }}
            />

            <div className="flex-shrink-0">
              <SelectInput
                value={
                  filterExcludeTags
                    ? filteredExcludeTagsDietaryPreferenceUnique
                    : filteredDietaryPreference
                }
                options={
                  filterExcludeTags
                    ? excludeDietaryPreferenceOptions
                    : dietaryPreferenceOptions
                }
                onChange={updateDietaryPreferenceFilters}
                excludeDefaultOption={true}
              />
            </div>

            <button
              onClick={() => setShowDietaryCounts(!showDietaryCounts)}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 transition-colors whitespace-nowrap flex items-center gap-2"
              title={
                showDietaryCounts
                  ? 'Collapse dietary counts in table'
                  : 'Expand dietary counts in table'
              }
            >
              {showDietaryCounts ? (
                <FaChevronUp size={12} />
              ) : (
                <FaChevronDown size={12} />
              )}
              {showDietaryCounts ? 'Collapse Dietary' : 'Expand Dietary'}
            </button>
          </div>
        </div>
      </div>

      {/* Staging Section */}
      {stagedMenus.length > 0 && (
        <div className="border-t border-gray-300 pt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Staged Menus ({stagedMenus.length})
            </h3>
          </div>

          <div className="overflow-x-auto">
            <div className="align-middle inline-block min-w-full">
              <div className="shadow overflow-hidden border border-gray-300 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-300">
                    <tr className="divide-x divide-gray-200">
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Chef
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Cuisines
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Has Mods
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Available Days
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Cutoff
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Earliest Pickup
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Max Items
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Item Count
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Min Payout
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        Dietary Counts
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-green-200 divide-y divide-gray-200">
                    {stagedMenus.map((menu) => (
                      <tr key={menu.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {menu.name}
                          <button
                            onClick={() => handleRemoveFromStaging(menu.id)}
                            disabled={isProcessingStaged}
                            className="px-2 py-1 bg-red-500 text-white rounded-md text-xs font-bold hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            title="Remove from staging"
                          >
                            Remove
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {menu.chef.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {menu.cuisines.join(', ')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {menu.hasMenuItemWithMods ? 'Yes' : 'No'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getAvailableDaysStr(menu)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCutoff(menu.cutoffDays, menu.cutoffHours)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {!menu.pickupHours
                            ? 'N/A'
                            : parseMilitaryTimeToAMPM(menu.pickupHours)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {menu.maxItems || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {menu.menuItems.length}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {menu.minGoPayout || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 flex flex-col">
                          {(() => {
                            const dietaryArray = getDietaryCountsArray(menu)
                            const displayItems = showDietaryCounts
                              ? dietaryArray
                              : dietaryArray.slice(0, 2)
                            const remainingCount = dietaryArray.length - 2

                            return (
                              <>
                                {displayItems.map((dietary) => (
                                  <span
                                    key={dietary}
                                    className="px-2 py-1 bg-blue-200 rounded-md text-center"
                                  >
                                    {dietary}
                                  </span>
                                ))}
                                {!showDietaryCounts && remainingCount > 0 && (
                                  <>... +{remainingCount} more</>
                                )}
                              </>
                            )
                          })()}
                        </td>
                      </tr>
                    ))}
                    {stagedMenus.length > 0 &&
                      (() => {
                        const {
                          totalItemCount,
                          dietarySummary,
                          cuisineSummary,
                        } = getStagingSummary()
                        const dietaryArray = Object.entries(dietarySummary).map(
                          ([dietary, count]) => `${dietary} (${count})`,
                        )
                        const displayItems = showDietaryCounts
                          ? dietaryArray
                          : dietaryArray.slice(0, 2)
                        const remainingCount = dietaryArray.length - 2

                        return (
                          <tr className="bg-gray-200 border-t-2 border-gray-400 font-semibold">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                              {stagedMenus.length} Menu(s)
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              —
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 flex flex-col">
                              {Object.entries(cuisineSummary).map(
                                ([cuisine, count]) => (
                                  <span
                                    key={cuisine}
                                    className="px-2 py-1 bg-green-200 rounded-md text-center font-semibold w-full"
                                  >
                                    {cuisine} ({count})
                                  </span>
                                ),
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              —
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              —
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              —
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              —
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              —
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                              {totalItemCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              —
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 flex flex-col">
                              <>
                                {displayItems.map((dietary) => (
                                  <span
                                    key={dietary}
                                    className="px-2 py-1 bg-blue-200 rounded-md text-center"
                                  >
                                    {dietary}
                                  </span>
                                ))}
                                {!showDietaryCounts && remainingCount > 0 && (
                                  <>... +{remainingCount} more</>
                                )}
                              </>
                            </td>
                          </tr>
                        )
                      })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <button
            onClick={handleFinalizeStaged}
            disabled={isProcessingStaged || stagedMenus.length === 0}
            className="px-4 py-2 mt-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isProcessingStaged
              ? 'Processing...'
              : `Finalize ${stagedMenus.length} Menu${
                  stagedMenus.length === 1 ? '' : 's'
                }`}
          </button>
        </div>
      )}

      {/* Search Results */}
      <div className="overflow-x-auto">
        <div className="align-middle inline-block min-w-full">
          <div className="shadow overflow-hidden border border-gray-300 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-300">
                <tr className="divide-x divide-gray-200">
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Chef
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Cuisines
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Has Mods
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Available Days
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Cutoff
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Earliest Pickup
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Max Items
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Item Count
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Min Payout
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Dietary Counts
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading && (
                  <tr>
                    <td colSpan={11} className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                )}
                {!isLoading && menuConcepts.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center py-4 text-gray-900">
                      No menus found.
                    </td>
                  </tr>
                )}
                {menuConcepts.map((menu) => (
                  <tr key={menu.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center space-x-2">
                        <span>{menu.name}</span>
                        <button
                          onClick={() => handleStageMenu(menu)}
                          disabled={
                            stagedMenus.find(
                              (staged) => staged.id === menu.id,
                            ) !== undefined
                          }
                          className={`ml-2 px-2 py-1 rounded-full text-xs font-bold focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                            stagedMenus.find((staged) => staged.id === menu.id)
                              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                              : 'bg-green-500 text-white hover:bg-green-600 focus:ring-green-500'
                          }`}
                          title={
                            stagedMenus.find((staged) => staged.id === menu.id)
                              ? 'Already staged'
                              : 'Stage this menu'
                          }
                        >
                          {stagedMenus.find((staged) => staged.id === menu.id)
                            ? '✓'
                            : '+'}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {menu.chef.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {menu.cuisines.join(', ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {menu.hasMenuItemWithMods ? 'Yes' : 'No'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getAvailableDaysStr(menu)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCutoff(menu.cutoffDays, menu.cutoffHours)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {!menu.pickupHours
                        ? 'N/A'
                        : parseMilitaryTimeToAMPM(menu.pickupHours)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {menu.maxItems || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {menu.menuItems.length}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {menu.minGoPayout || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 flex flex-col">
                      {(() => {
                        const dietaryArray = getDietaryCountsArray(menu)
                        const displayItems = showDietaryCounts
                          ? dietaryArray
                          : dietaryArray.slice(0, 2)
                        const remainingCount = dietaryArray.length - 2

                        return (
                          <>
                            {displayItems.map((dietary) => (
                              <span
                                key={dietary}
                                className="px-2 py-1 bg-blue-200 rounded-md text-center"
                              >
                                {dietary}
                              </span>
                            ))}
                            {!showDietaryCounts && remainingCount > 0 && (
                              <>... +{remainingCount} more</>
                            )}
                          </>
                        )
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PickMenuModal
